const DEFAULT_MAX_DIMENSION = 512;
const DEFAULT_JPEG_QUALITY = 0.65;

export interface CompressOptions {
  /** Longest side, in pixels. Default 512. Raise for close-up photos
   *  (face / head) where the product's detail IS on the face — 1536 gives
   *  the try-on model enough pixels to render sharp frame edges. */
  maxDimension?: number;
  /** JPEG quality, 0–1. Default 0.65. Raise to 0.85+ for close-ups so the
   *  face region isn't macroblocked before Gemini renders the frame. */
  quality?: number;
}

/** Compresses and resizes an image file, returns a base64 data URL. */
export function compressImage(file: File, opts: CompressOptions = {}): Promise<string> {
  const maxDim = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const quality = opts.quality ?? DEFAULT_JPEG_QUALITY;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          let { width, height } = img;

          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Canvas context not available"));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/** Validates that a file is an accepted image type. AVIF is accepted for
 *  upload — Gemini's API doesn't support AVIF directly, but compressImage
 *  re-encodes the canvas as JPEG before we ship it, so AVIF inputs land as
 *  JPEG on the wire. */
export function isValidImageFile(file: File): boolean {
  const accepted = ["image/jpeg", "image/png", "image/webp", "image/avif"];
  return accepted.includes(file.type);
}

/**
 * Pre-upload age check. Sends the photo to the backend's
 * /api/v1/sizing/age-check endpoint. Backend uses Gemini Flash-Lite to
 * classify whether the subject is 18+. The bytes are NOT persisted by the
 * backend (no disk write, no logging of full base64).
 *
 * Accepts EITHER a File OR a base64 data URL. When a File is provided we
 * compress it to a tight 384px / 60% JPEG thumb on the way out — Gemini
 * Flash-Lite only needs facial maturity, not pixel detail, and a smaller
 * payload trims a noticeable chunk of network + decode latency end-to-end.
 *
 * Fail-open: if the network call errors or the backend is unreachable, we
 * return isAdult=true so a network hiccup doesn't block legitimate users.
 * The existing self-cert checkbox is the defense-in-depth fallback.
 */
export async function checkAgeBeforeUpload(
  imageOrBase64: string | File,
  apiUrl: string,
  apiKey: string,
): Promise<{ isAdult: boolean; confidence: "high" | "low"; reasoning?: string; detectedGender?: "male" | "female" | "unknown" }> {
  try {
    const base64 = typeof imageOrBase64 === "string"
      ? imageOrBase64
      : await compressImage(imageOrBase64, { maxDimension: 384, quality: 0.6 });
    const resp = await fetch(`${apiUrl}/api/v1/sizing/age-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ bodyImage: base64 }),
    });
    if (!resp.ok) {
      console.warn(`[PS-SDK:AgeCheck] HTTP ${resp.status} — failing open`);
      return { isAdult: true, confidence: "low" };
    }
    const data = await resp.json();
    return {
      isAdult: data?.isAdult !== false, // anything other than explicit false → allow
      confidence: data?.confidence === "high" ? "high" : "low",
      detectedGender: data?.detectedGender === "male" || data?.detectedGender === "female" || data?.detectedGender === "unknown"
        ? data.detectedGender
        : undefined,
      reasoning: typeof data?.reasoning === "string" ? data.reasoning : undefined,
    };
  } catch (err) {
    console.warn(`[PS-SDK:AgeCheck] error — failing open:`, err);
    return { isAdult: true, confidence: "low" };
  }
}
